import { Inject, Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';

/**
 * Platform-owned client settings, served by the operator instead of being hardcoded here.
 *
 * Mirrors the rider app's PollingSettingsService and reads the same anonymous, minute-cached endpoint
 * (GET /api/public/polling-settings). This app only needs a few rules from it — the location-drift distance
 * and which delivery types a free delivery may cover — not the poll intervals, which are still literals in
 * their components; so the shape is deliberately narrow rather than a full copy of the row.
 *
 * RULES, in priority order:
 *  1. NEVER BLOCK. The fetch is fire-and-forget; callers get a usable number immediately.
 *  2. SURVIVE A RESTART. The last good response is cached, so a phone opening the app on a bad
 *     connection still uses the operator's values.
 *  3. FAIL TO THE SHIPPED DEFAULTS, which match the server's — so adopting this changes nothing until
 *     an operator edits something.
 */
@Injectable({ providedIn: 'root' })
export class PlatformSettingsService {
  private readonly http = inject(HttpClient);
  private readonly isBrowser: boolean;

  private static readonly CACHE_KEY = 'orderyaa.platform.settings.v1';

  /** Identical to PlatformPollingSettings.Default* on the server. */
  private static readonly DEFAULTS = {
    customerLocationDriftMeters: 200,
    /** Delivery types a free delivery (voucher, coins, free-delivery coupon) may cover — the server's
     *  FreeDeliveryTiers names. Standard only unless the operator opts others in. */
    freeDeliveryTiers: ['Standard'] as string[],
  };

  private readonly values = signal({ ...PlatformSettingsService.DEFAULTS });

  constructor(@Inject(PLATFORM_ID) platformId: Object) {
    this.isBrowser = isPlatformBrowser(platformId);
    if (!this.isBrowser) return;
    this.restoreCached();
    void this.refresh();
  }

  /**
   * How far the device may drift from the chosen delivery spot before we say so. 0 = the operator has
   * turned the check off, which callers must honour rather than substituting a default.
   */
  get locationDriftMeters(): number {
    const v = this.values().customerLocationDriftMeters;
    return typeof v === 'number' && v >= 0 ? v : PlatformSettingsService.DEFAULTS.customerLocationDriftMeters;
  }

  /**
   * Whether a free delivery — a voucher or coins, here — may cover this delivery option. The operator's call
   * (App Settings), standard only by default; the server refuses a reward on an option left out, so checkout
   * must not offer one.
   */
  freeDeliveryCovers(option: 'standard' | 'priority'): boolean {
    const tiers = this.values().freeDeliveryTiers ?? PlatformSettingsService.DEFAULTS.freeDeliveryTiers;
    return tiers.some((t) => t.toLowerCase() === option);
  }

  /** Failure is silent and total — whatever values we already hold stand. */
  async refresh(): Promise<void> {
    const platformCompanyId = environment.platformCompanyId;
    // No operator configured for this deployment: the endpoint would only echo the defaults we already
    // hold, so skip the request rather than fetch for nothing.
    if (!platformCompanyId) return;

    try {
      const url = `${environment.apiBaseUrl}/api/public/polling-settings`
        + `?platformCompanyId=${encodeURIComponent(platformCompanyId)}`;
      const res = await firstValueFrom(
        this.http.get<{ data?: Partial<typeof PlatformSettingsService.DEFAULTS> }>(url));
      if (!res?.data) return;
      // Merge, so a field the server stops sending falls back to its default rather than becoming
      // undefined. Only the keys we declare are kept — the response carries the whole row.
      const next = { ...PlatformSettingsService.DEFAULTS };
      const drift = res.data.customerLocationDriftMeters;
      if (typeof drift === 'number') next.customerLocationDriftMeters = drift;
      const tiers = res.data.freeDeliveryTiers;
      if (Array.isArray(tiers)) next.freeDeliveryTiers = tiers.filter((t): t is string => typeof t === 'string');
      this.values.set(next);
      localStorage.setItem(PlatformSettingsService.CACHE_KEY, JSON.stringify(next));
    } catch {
      /* offline or endpoint unavailable — cached/default values stand */
    }
  }

  private restoreCached(): void {
    try {
      const raw = localStorage.getItem(PlatformSettingsService.CACHE_KEY);
      if (!raw) return;
      this.values.set({ ...PlatformSettingsService.DEFAULTS, ...JSON.parse(raw) });
    } catch {
      /* corrupt cache — defaults already in place */
    }
  }
}
